import { applyTenantRequestHeaders } from '../tenantRequestContext';

export type LaunchAssetClass = 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO';

export type ReplayLaunchControlsStatus = {
  replayEnabled: boolean;
  enabledAssetClasses: LaunchAssetClass[];
  navigationLayoutMode: 'both' | 'side_only' | 'top_only';
  themeMode?: 'light_only' | 'dark_only' | 'light_dark';
  resolvedFrom?: string | null;
  tenantSlug?: string | null;
};

function normalizeNavigationLayoutMode(value: unknown): 'both' | 'side_only' | 'top_only' {
  const token = String(value ?? '').trim().toLowerCase();
  if (token === 'side_only' || token === 'top_only') return token;
  return 'both';
}

function normalizeAssetClassToken(value: unknown): LaunchAssetClass | null {
  const token = String(value ?? '').trim().toUpperCase();
  if (!token) return null;
  if (token === 'STK' || token === 'STOCK' || token === 'STOCKS' || token === 'EQUITY' || token === 'EQUITIES') return 'STOCKS';
  if (token === 'FUT' || token === 'FUTURE' || token === 'FUTURES') return 'FUTURES';
  if (token === 'CASH' || token === 'FOREX' || token === 'FX' || token === 'CURRENCY' || token === 'CURRENCIES') return 'FOREX';
  if (token === 'CRYPTO' || token === 'CRYPTOCURRENCY' || token === 'CRYPTOCURRENCIES' || token === 'DIGITAL_ASSET' || token === 'DIGITAL_ASSETS') return 'CRYPTO';
  return null;
}

function normalizeReplayEnabled(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  const token = String(value ?? '').trim().toLowerCase();
  if (!token) return true;
  if (token === '0' || token === 'false' || token === 'no' || token === 'off') return false;
  return true;
}

function normalizeEnabledAssetClasses(value: unknown): LaunchAssetClass[] {
  if (!Array.isArray(value)) return [];
  const normalized = value
    .map((entry) => normalizeAssetClassToken(entry))
    .filter((entry): entry is LaunchAssetClass => Boolean(entry));
  return Array.from(new Set(normalized));
}

export function parseReplayLaunchControlsStatus(payload: unknown): ReplayLaunchControlsStatus | null {
  if (!payload || typeof payload !== 'object') return null;
  const obj = payload as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(obj, 'replayEnabled') && !Object.prototype.hasOwnProperty.call(obj, 'enabledAssetClasses')) {
    return null;
  }

  const replayEnabled = normalizeReplayEnabled(obj.replayEnabled);
  const enabledAssetClasses = normalizeEnabledAssetClasses(obj.enabledAssetClasses);
  const navigationLayoutMode = normalizeNavigationLayoutMode(obj.navigationLayoutMode || obj.navigation_layout_mode);
  const themeModeToken = String(obj.themeMode || obj.theme_mode || '').trim().toLowerCase();
  return {
    replayEnabled,
    enabledAssetClasses,
    navigationLayoutMode,
    themeMode: ['light_only', 'dark_only', 'light_dark'].includes(themeModeToken) ? themeModeToken as ReplayLaunchControlsStatus['themeMode'] : 'light_dark',
    resolvedFrom: typeof obj.resolvedFrom === 'string' ? obj.resolvedFrom : null,
    tenantSlug: typeof obj.tenantSlug === 'string' ? obj.tenantSlug : null,
  };
}

export async function fetchReplayLaunchControlsStatus(options?: { signal?: AbortSignal }): Promise<ReplayLaunchControlsStatus | null> {
  const headers: Record<string, string> = {};
  applyTenantRequestHeaders(headers);

  const res = await fetch('/api/market/provider-status', {
    headers,
    signal: options?.signal,
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok) return null;
  return parseReplayLaunchControlsStatus(payload);
}

export function mapSecTypeToLaunchAssetClass(secType: unknown): LaunchAssetClass {
  return normalizeAssetClassToken(secType) || 'STOCKS';
}

export function isLaunchAssetClassEnabled(assetClass: LaunchAssetClass, status: ReplayLaunchControlsStatus | null): boolean {
  if (!status) return true;
  return status.enabledAssetClasses.includes(assetClass);
}

export function getReplayBlockedMessage(options: {
  status: ReplayLaunchControlsStatus | null;
  assetClass: LaunchAssetClass;
}): string | null {
  const status = options.status;
  if (!status) return null;

  if (!status.replayEnabled) {
    return 'Replay is temporarily unavailable. You can still review trade details and analytics.';
  }

  if (!isLaunchAssetClassEnabled(options.assetClass, status)) {
    if (options.assetClass === 'FUTURES') return 'Futures replay is not currently supported.';
    if (options.assetClass === 'FOREX') return 'Forex is not currently enabled for this account.';
    if (options.assetClass === 'CRYPTO') return 'Crypto is not currently enabled for this account.';
    return 'Stocks are not currently enabled for this account.';
  }

  return null;
}
